import { FORMAT_SAMPLE_TYPE, type TexFormat } from './types.js';

/**
 * WGSL module registry with `#include` resolution, `${TEMPLATE}` substitution,
 * explicit bind group layouts and a compute-pipeline cache.
 *
 * Several kernels here are format-polymorphic (the same box blur runs over
 * `rgba32float` moments, `rg32float` coefficients and `r32float` alpha). WGSL has
 * no generics and no preprocessor, so we stamp out a specialised module per
 * format combination and cache the compiled pipeline by its full key.
 */

export type Defines = Record<string, string | number>;

/** Declarative description of one `@group(0) @binding(n)` slot. */
export type BindingSpec =
  /** `texture_2d<f32>` / `texture_2d<u32>`, always read with `textureLoad`. */
  | { kind: 'texture'; format: TexFormat }
  /** `texture_storage_2d<F, write>` or `<F, read_write>`. */
  | { kind: 'storageTexture'; format: TexFormat; access?: 'write-only' | 'read-write' }
  /** `var<uniform>` block. */
  | { kind: 'uniform' }
  /** `var<storage, read>` / `var<storage, read_write>` buffer. */
  | { kind: 'buffer'; access?: 'read-only' | 'read-write' }
  | { kind: 'sampler'; type?: GPUSamplerBindingType };

export interface KernelRequest {
  /** Name the module was registered under. */
  module: string;
  /** Entry point inside the module; defaults to `main`. */
  entryPoint?: string;
  /** Layout of `@group(0)`, in binding order. */
  bindings: readonly BindingSpec[];
  /** `${NAME}` placeholders substituted before compilation. */
  defines?: Defines;
  /** WGSL pipeline-overridable constants. */
  constants?: Record<string, number>;
  label?: string;
}

const INCLUDE_RE = /^[ \t]*#include[ \t]+"([^"]+)"[ \t]*$/gm;
const PLACEHOLDER_RE = /\$\{([A-Za-z_][A-Za-z0-9_]*)\}/g;

export class ShaderLibrary {
  readonly #sources = new Map<string, string>();
  readonly #modules = new Map<string, GPUShaderModule>();
  readonly #layouts = new Map<string, GPUBindGroupLayout>();
  readonly #pipelines = new Map<string, GPUComputePipeline>();

  constructor(private readonly device: GPUDevice) {}

  /** Registers (or replaces) a WGSL source under `name`. */
  register(name: string, source: string): void {
    this.#sources.set(name, source);
  }

  registerAll(sources: Record<string, string>): void {
    for (const [name, source] of Object.entries(sources)) this.register(name, source);
  }

  has(name: string): boolean {
    return this.#sources.has(name);
  }

  /** Names of every registered module, sorted - used by the shader tests. */
  moduleNames(): string[] {
    return [...this.#sources.keys()].sort();
  }

  /**
   * Resolves `#include`s and substitutes `${NAME}` placeholders.
   *
   * Exposed (rather than private) so the test suite can run every module through
   * a WGSL parser without needing a GPU.
   */
  resolve(name: string, defines: Defines = {}): string {
    const seen = new Set<string>();
    const expand = (moduleName: string): string => {
      const source = this.#sources.get(moduleName);
      if (source === undefined) {
        throw new Error(`[gpu-core] unknown WGSL module "${moduleName}"`);
      }
      // A module may be included by several others; emit it once per resolution.
      if (seen.has(moduleName)) return '';
      seen.add(moduleName);
      return source.replace(INCLUDE_RE, (_match, included: string) => expand(included));
    };

    const body = expand(name);
    return body.replace(PLACEHOLDER_RE, (match, key: string) => {
      const value = defines[key];
      if (value === undefined) {
        throw new Error(`[gpu-core] WGSL module "${name}" needs a value for \${${key}}`);
      }
      return String(value);
    });
  }

  #moduleFor(name: string, defines: Defines): GPUShaderModule {
    const key = `${name}#${stableKey(defines)}`;
    let module = this.#modules.get(key);
    if (!module) {
      module = this.device.createShaderModule({ label: key, code: this.resolve(name, defines) });
      this.#modules.set(key, module);
    }
    return module;
  }

  /** Builds (and caches) an explicit `@group(0)` layout from a binding spec. */
  bindGroupLayout(bindings: readonly BindingSpec[]): GPUBindGroupLayout {
    const key = bindings.map(bindingKey).join(';');
    let layout = this.#layouts.get(key);
    if (!layout) {
      layout = this.device.createBindGroupLayout({
        label: key,
        entries: bindings.map((spec, binding) => ({
          binding,
          visibility: GPUShaderStage.COMPUTE,
          ...bindingLayout(spec),
        })),
      });
      this.#layouts.set(key, layout);
    }
    return layout;
  }

  /** Returns a cached compute pipeline for the request, compiling on first use. */
  pipeline(request: KernelRequest): GPUComputePipeline {
    const entryPoint = request.entryPoint ?? 'main';
    const bindingKeyStr = request.bindings.map(bindingKey).join(';');
    const key = [
      request.module,
      entryPoint,
      stableKey(request.defines ?? {}),
      stableKey(request.constants ?? {}),
      bindingKeyStr,
    ].join('|');

    let pipeline = this.#pipelines.get(key);
    if (!pipeline) {
      const module = this.#moduleFor(request.module, request.defines ?? {});
      const bindGroupLayout = this.bindGroupLayout(request.bindings);
      pipeline = this.device.createComputePipeline({
        label: request.label ? `${key} (${request.label})` : key,
        layout: this.device.createPipelineLayout({
          label: key,
          bindGroupLayouts: [bindGroupLayout],
        }),
        compute: {
          module,
          entryPoint,
          ...(request.constants ? { constants: request.constants } : {}),
        },
      });
      this.#pipelines.set(key, pipeline);
    }
    return pipeline;
  }

  /**
   * Surfaces WGSL compilation diagnostics. Chrome reports them asynchronously,
   * so the workbench calls this once after warm-up rather than per dispatch.
   */
  async compilationMessages(): Promise<Array<{ module: string; messages: GPUCompilationMessage[] }>> {
    const out: Array<{ module: string; messages: GPUCompilationMessage[] }> = [];
    for (const [key, module] of this.#modules) {
      const info = await module.getCompilationInfo();
      if (info.messages.length > 0) out.push({ module: key, messages: [...info.messages] });
    }
    return out;
  }
}

function bindingKey(spec: BindingSpec): string {
  switch (spec.kind) {
    case 'texture':
      return `t:${FORMAT_SAMPLE_TYPE[spec.format]}`;
    case 'storageTexture':
      return `s:${spec.format}:${spec.access ?? 'write-only'}`;
    case 'uniform':
      return 'u';
    case 'buffer':
      return `b:${spec.access ?? 'read-only'}`;
    case 'sampler':
      return `smp:${spec.type ?? 'filtering'}`;
  }
}

function bindingLayout(spec: BindingSpec): Omit<GPUBindGroupLayoutEntry, 'binding' | 'visibility'> {
  switch (spec.kind) {
    case 'texture':
      return { texture: { sampleType: FORMAT_SAMPLE_TYPE[spec.format], viewDimension: '2d' } };
    case 'storageTexture':
      return {
        storageTexture: {
          access: spec.access ?? 'write-only',
          format: spec.format,
          viewDimension: '2d',
        },
      };
    case 'uniform':
      return { buffer: { type: 'uniform' } };
    case 'buffer':
      return { buffer: { type: spec.access === 'read-write' ? 'storage' : 'read-only-storage' } };
    case 'sampler':
      return { sampler: { type: spec.type ?? 'filtering' } };
  }
}

/** Deterministic stringification so cache keys do not depend on key order. */
function stableKey(record: Record<string, string | number>): string {
  const keys = Object.keys(record).sort();
  if (keys.length === 0) return '';
  return keys.map((k) => `${k}=${record[k]}`).join(',');
}
